import * as React from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Col, ListGroup, ListGroupItem, Navbar, NavbarBrand, Row } from 'reactstrap';
import { FaArrowDown, FaArrowUp, FaPlus, FaPrint } from 'react-icons/fa';
import { TablePagination } from '../TablePagination/TablePagination';
import { useDefaultColumn } from '../../hooks/ReactTableHooks';
import { Column, TableInstance, useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';
import { authAxios } from '../../services/AxiosService';
import { useLocation, useParams } from 'react-router';
import { UsersContext } from '../../contexts/UsersContext';
import { FileContext } from './FileContext';
import { TableGlobalFilter } from '../TableGlobalFilter/TableGlobalFilter';
import { IUser } from '../Users/UsersTable';
import { IDocument } from './Documents/Documents';
import { formatDateTime } from '../../formatters/DateTimeFormatter';
import { sanitizeHTMLConfig } from '../../contexts/DomainContext';
import {
  FileIncidentCheckboxCell,
  FileIncidentMetadataCell,
  FileIncidentTextCell,
  IFileIncidentCell,
} from '../../components/Files/FileIncident';
import { FileIncidentsDetails } from '../../components/Files/FileIncidentsDetails';

const DOMPurify = require('dompurify');

export interface IIncident {
  incidentId: string;
  text: string;
  date: number;
  userCreated: string;
  dateLastUpdated: number;
  userUpdated: string;
  documents?: IDocument[];
  isSelected?: boolean;
  fileId?: string;
  fileNumber?: string;
  userCanEdit: boolean;
}

export const FileIncidents: React.FC = () => {
  const params = useParams<{ fileId: string }>();
  const location = useLocation<{ deletedIncidentId?: string }>();
  const usersContext = useContext(UsersContext);
  const fileContext = useContext(FileContext);
  const users = usersContext.users;
  const [showNewIncident, setShowNewIncident] = useState(false);
  const [printOption, setPrintOption] = useState('');
  const [isSelectingIncidents, setIsSelectingIncidents] = useState(false);
  const [selectedIncidents, setSelectedIncidents] = useState<string[]>([]);
  const [incidents, setIncidents] = useState<IIncident[]>([]);

  useEffect(() => {
    if (printOption !== '') {
      window.print();
      setPrintOption('');
      setIsSelectingIncidents(false);
      setSelectedIncidents([]);
    }
  }, [printOption]);

  useEffect(() => {
    const deletedIncidentId = location?.state?.deletedIncidentId;
    authAxios.get('/api/files/admin/' + params.fileId + '/incidents').then((response) => {
      if (response.data.status === 'OK') {
        fileContext.setIncidents(response.data.incidents.filter((e: IIncident) => e.incidentId !== deletedIncidentId));
        setIncidents(() => {
          return response.data.incidents.filter((e: IIncident) => e.incidentId !== deletedIncidentId && e.fileId);
        });
      }
    });
  }, [params.fileId]);

  useEffect(() => {
    setIncidents(fileContext.incidents);
  }, [fileContext.incidents]);

  const columns: Column<IIncident>[] = useMemo(
    () => [
      {
        Header: 'Id',
        accessor: 'incidentId',
      },
      {
        Header: 'User',
        accessor: (f: IIncident) => {
          if (f.userCreated) {
            const user = users.find((l: IUser) => l.userId === f.userCreated);
            return user?.firstName + ' ' + user?.lastName;
          }
        },
        id: 'userCreated',
        width: 100,
      },
      {
        Header: '',
        accessor: 'isSelected',
        width: 50,
        // eslint-disable-next-line react/display-name
        Cell: (cell: IFileIncidentCell) => {
          return (
            <FileIncidentCheckboxCell
              cell={cell}
              selectedIncidents={selectedIncidents}
              setSelectedIncidents={setSelectedIncidents}
            />
          );
        },
      },
      {
        Header: 'Date Added',
        accessor: 'date',
        width: 250,
        // eslint-disable-next-line react/display-name
        Cell: (cell: IFileIncidentCell) => {
          return <FileIncidentMetadataCell cell={cell} />;
        },
      },
      {
        Header: 'Text',
        accessor: 'text',
        // eslint-disable-next-line react/display-name
        Cell: (cell: IFileIncidentCell) => {
          return (
            <FileIncidentTextCell cell={cell} printIncident={() => setPrintOption(cell.row.original.incidentId)} />
          );
        },
      },
    ],
    [users, fileContext.incidents, incidents, selectedIncidents]
  );

  const data: IIncident[] = useMemo(() => incidents, [incidents, fileContext.incidents]);

  const defaultColumn = useDefaultColumn();

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    visibleColumns,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    preGlobalFilteredRows,
    setGlobalFilter,
    setHiddenColumns,
    state,
    state: { pageIndex, pageSize },
  }: TableInstance<IIncident> = useTable(
    {
      columns,
      data,
      defaultColumn,
      initialState: {
        pageSize: 20,
        sortBy: [
          {
            id: 'dateCreated',
            desc: true,
          },
        ],
        hiddenColumns: isSelectingIncidents
          ? ['userCreated', 'incidentId']
          : ['userCreated', 'incidentId', 'isSelected'],
      },
    },
    // useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  return (
    <>
      <div>
        <header className={'report-header hide-printable'}>
          <h2 className='text-center'>CYFN File {fileContext.file.fileNumber} Incidents</h2>
        </header>
        <ListGroup>
          {data.map((n) => (
            <ListGroupItem
              key={n.incidentId}
              className={
                printOption === n.incidentId ||
                printOption === 'all' ||
                (printOption === 'selected' && selectedIncidents.includes(n.incidentId))
                  ? 'hide-printable'
                  : 'hide-always'
              }
            >
              <Row>
                <Col md={12} lg={2} className={'text-lg-right'}>
                  <strong>
                    {users.find((u) => u.userId === n.userCreated)
                      ? users.find((u) => u.userId === n.userCreated)?.lastName.toUpperCase() +
                        ', ' +
                        users.find((u) => u.userId === n.userCreated)?.firstName
                      : ''}
                  </strong>
                  <div className={'text-muted small mb-2'}>{formatDateTime(new Date(n.date))}</div>
                </Col>
                <Col md={8} lg={7} xl={6} className={'mb-3 mb-md-0'}>
                  <main dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(n.text, sanitizeHTMLConfig) }} />
                </Col>
                <Col>
                  {n.documents && n.documents.length > 0 && (
                    <div>
                      <h6>Documents</h6>
                      <ul>
                        {n.documents.map((d) => (
                          <li key={d.documentId}>
                            <Button
                              color={'link'}
                              className={'p-0'}
                              disabled={!d.uploadId}
                              style={{ wordBreak: 'break-all', textAlign: 'left' }}
                            >
                              {d.fileName}
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Col>
              </Row>
            </ListGroupItem>
          ))}
        </ListGroup>
      </div>
      <div className={'mb-5 no-print'}>
        {!showNewIncident && (
          <Navbar color={'light'} light={true} expand={'xs'} className={'border-bottom d-flex flex-wrap sticky-top'}>
            <NavbarBrand className={'w-mobile-100'}>Incidents</NavbarBrand>
            <div
              className={'d-flex flex-grow-1 justify-content-end flex-wrap navbar-actions'}
              style={{ rowGap: '0.5rem', columnGap: '0.5rem' }}
            >
              {isSelectingIncidents && (
                <>
                  <Button
                    className={'w-mobile-100'}
                    color={'link'}
                    onClick={() => {
                      setIsSelectingIncidents(false);
                      setSelectedIncidents([]);
                      setHiddenColumns(['userCreated', 'incidentId', 'isSelected']);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    className={'w-mobile-100'}
                    color={'warning'}
                    onClick={() => {
                      setPrintOption('selected');
                    }}
                  >
                    <FaPrint /> Print Selected Incidents
                  </Button>
                </>
              )}
              {!isSelectingIncidents && (
                <>
                  <Button
                    outline={true}
                    className={'w-mobile-100'}
                    onClick={() => {
                      setIsSelectingIncidents(true);
                      setHiddenColumns(['userCreated', 'incidentId']);
                    }}
                  >
                    Select Incidents
                  </Button>
                  <Button className={'w-mobile-100'} color={'warning'} onClick={() => setPrintOption('all')}>
                    <FaPrint /> Print All Incidents
                  </Button>
                  <Button color={'primary'} className={'w-mobile-100'} onClick={() => setShowNewIncident(true)}>
                    <FaPlus /> New Incident
                  </Button>
                </>
              )}
            </div>
          </Navbar>
        )}
        {showNewIncident && (
          <FileIncidentsDetails onSave={() => setShowNewIncident(false)} onCancel={() => setShowNewIncident(false)} />
        )}
        <table className={'table table-bordered'} {...getTableProps()}>
          <thead style={{ overflow: 'visible' }} className={'no-print'}>
            {headerGroups.map((headerGroup: any, index: number) => (
              <tr key={`files-table-thead-tr-${index}`} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.slice(0, -1).map((column: any) => (
                  <th
                    key={`files-table-thead-tr-${index}-${column.id}`}
                    width={column.width}
                    style={{ maxWidth: column.width }}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    {column.render('Header')}
                    {column.isSorted &&
                      (column.isSortedDesc ? <FaArrowDown className={'ml-2'} /> : <FaArrowUp className={'ml-2'} />)}
                  </th>
                ))}
                <TableGlobalFilter
                  preGlobalFilteredRows={preGlobalFilteredRows}
                  globalFilter={state.globalFilter}
                  setGlobalFilter={setGlobalFilter}
                  colSpan={visibleColumns.length}
                  label={{ singular: 'Incident', plural: 'Incidents' }}
                />
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} style={{ overflow: 'visible' }} id={'table-body'}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} key={`files-table-tr-${row.id}`}>
                  {row.cells.map((cell: any, index: number) => {
                    return (
                      <td
                        key={`files-table-td-${cell.row.id}-${cell.column.id}`}
                        colSpan={index === row.cells.length - 1 ? 2 : 1}
                      >
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        <TablePagination
          pageCount={pageCount}
          pageOptions={pageOptions}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          gotoPage={gotoPage}
          previousPage={previousPage}
          nextPage={nextPage}
          setPageSize={setPageSize}
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageSizes={[20, 50, 100, 500]}
        />
      </div>
    </>
  );
};
